import {
	useCallback, useMemo, useState,
} from 'react';

import { useProvinceListQuery } from '@/modules/shared/application';

import { LocationRegionSchema } from '@packages/contracts';

import type { NestedCategory } from '../composites/NestedCheckbox/NestedCheckbox';
import { categories } from '../constants';

/**
 * Custom hook for job locations selector state
 * Manages state for the locations selector with region-based filtering
 */
export const useJobLocationsSelectorState = () => {
	const [ group, setGroup ] = useState<null | string>(null);
	const region = LocationRegionSchema.safeParse(group).data ?? undefined;

	const { data: provinces, isLoading } = useProvinceListQuery(region, { enabled: !!region });

	const [ selectedIds, setSelectedIds ] = useState<string[]>([]);
	const selectedCategory = useMemo(() => categories.find(c => c.value === group), [ group ]);

	const collections = useMemo<NestedCategory[]>(
		() => {
			const items = (provinces ?? []).map(p => ({
				id: p.id,

				children: [],
				label:    p.localized.en,
			}));

			if (!region || !selectedCategory) return items; // flat = no parent checkbox

			return [
				{
					id: String(region),

					children: items,
					label:    selectedCategory.label.charAt(0).toUpperCase() + selectedCategory.label.slice(1),
				},
			];
		},
		[ provinces, region, selectedCategory ],
	);

	const flattenIds = useCallback((n: NestedCategory): string[] =>
		// @ts-expect-error fix later
		[ n.id, ...(n.children?.flatMap(flattenIds) ?? []) ], []);

	const handleToggle = useCallback((id: string, checked: boolean) => {
		const findNode = (nodes: NestedCategory[]): NestedCategory | undefined =>
			// @ts-expect-error fix later
			nodes.find(n => n.id === id) || nodes.flatMap(n => n.children ?? []).find((n: NestedCategory) => n.id === id);

		const target = findNode(collections);

		if (target?.children?.length) {
			const ids = flattenIds(target).filter(x => x !== target.id);
			setSelectedIds((prev) => {
				const set = new Set(prev);

				if (checked) ids.forEach(x => set.add(x));
				else ids.forEach(x => set.delete(x));

				return Array.from(set);
			});

			return;
		}

		setSelectedIds(prev => (checked ? Array.from(new Set([ id, ...prev ])) : prev.filter(x => x !== id)));
	}, [ collections, flattenIds ]);

	return {
		collections,
		group,
		handleToggle,
		isLoading,
		selectedIds,
		setGroup,
	};
};
